import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import HomeService from "../Services/HomeService";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "rgb(131, 144, 155)",
  margin: "0px 0px 10px 0px",
  fontWeight: "bold",
};

function AddDealerSection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchingDealers, setFetchingDealers] = useState(false);
  const [fetchingProperties, setFetchingProperties] = useState(false);
  const [dealerNames, setDealerNames] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);

  const [formData, setFormData] = useState({
    builderName: "",
    estdYear: "",
    totalProjects: "",
    shortDescription: "",
    properties: [],
  });

  // Fetch all dealer names on component mount
  useEffect(() => {
    fetchDealerNames();
  }, []);

  const fetchDealerNames = async () => {
    setFetchingDealers(true);
    try {
      const res = await HomeService.getAllDealerName();
      console.log("Dealer names response:", res);

      if (res?.status) {
        // API returns array of strings in res.data
        const dealerOptions = res.data.map((dealerName, index) => ({
          value: dealerName, // Use dealer name as value
          label: dealerName, // Use dealer name as label
          id: index, // For React key
        }));
        setDealerNames(dealerOptions);
      } else {
        toast.error(res?.message || "Failed to fetch dealer names");
      }
    } catch (error) {
      console.error("Error fetching dealer names:", error);
      toast.error("Error loading dealer names");
    } finally {
      setFetchingDealers(false);
    }
  };

  const handleDealerChange = async (selectedOption) => {
    if (!selectedOption) {
      setFormData((prev) => ({ ...prev, builderName: "" }));
      setProperties([]);
      setSelectedProperties([]);
      return;
    }

    // Set the builder name in form data
    setFormData((prev) => ({
      ...prev,
      builderName: selectedOption.value,
      properties: [], // Reset properties when dealer changes
    }));

    // Clear previous properties and selection
    setProperties([]);
    setSelectedProperties([]);

    // Fetch properties for selected dealer
    setFetchingProperties(true);
    try {
      const data = { dealerName: selectedOption.value };
      const res = await HomeService.getPropertiesByDealerName(data);
      console.log("Properties response:", res);

      if (res?.status && res.data) {
        // Assuming res.data is an array of property objects
        // Filter properties that have aptName field and map to react-select format
        const propertyOptions = res.data
          .filter(
            (property) => property.aptName && property.aptName.trim() !== ""
          ) // Only include properties with non-empty aptName
          .map((property) => ({
            value:
              property._id ||
              property.id ||
              property.propertyId ||
              property.aptName,
            label: property.aptName,
            originalData: property,
          }));

        setProperties(propertyOptions);

        // Show info about filtered properties
        if (propertyOptions.length === 0) {
          toast.error(
            "No properties with apartment names found for this dealer"
          );
        } else {
          toast.success(
            `Found ${propertyOptions.length} properties with apartment names`
          );
        }
      } else {
        toast.error(res?.message || "Failed to fetch properties");
        setProperties([]);
      }
    } catch (error) {
      console.error("Error fetching properties:", error);
      toast.error("Error loading properties");
      setProperties([]);
    } finally {
      setFetchingProperties(false);
    }
  };

  const handlePropertyChange = (selectedOptions) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setSelectedProperties(selectedOptions || []);
    setFormData((prev) => ({ ...prev, properties: selectedValues }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.builderName.trim()) {
      toast.error("Please select a dealer/builder");
      return;
    }

    if (!formData.estdYear.trim()) {
      toast.error("Please enter establishment year");
      return;
    }

    if (!formData.totalProjects.trim()) {
      toast.error("Please enter total projects");
      return;
    }

    if (!formData.shortDescription.trim()) {
      toast.error("Please enter short description");
      return;
    }

    if (formData.properties.length === 0) {
      toast.error("Please select at least one property");
      return;
    }

    // Validate year (1900-current year)
    const currentYear = new Date().getFullYear();
    const estdYear = parseInt(formData.estdYear);
    if (isNaN(estdYear) || estdYear < 1900 || estdYear > currentYear) {
      toast.error(`Please enter a valid year between 1900 and ${currentYear}`);
      return;
    }

    // Validate total projects (positive number)
    const totalProjects = parseInt(formData.totalProjects);
    if (isNaN(totalProjects) || totalProjects <= 0) {
      toast.error("Total projects must be a positive number");
      return;
    }

    setLoading(true);

    // Prepare data according to Postman body structure
    const dealerSectionData = {
      builderName: formData.builderName.trim(),
      estdYear: formData.estdYear.trim(),
      totalProjects: formData.totalProjects.trim(),
      shortDescription: formData.shortDescription.trim(),
      properties: formData.properties,
    };

    console.log("Dealer Section Data to Submit:", dealerSectionData);

    try {
      const res = await HomeService.createDealerSection(dealerSectionData);
      if (res?.status) {
        toast.success("Dealer section added successfully!");
        clearForm();
        // Navigate to dealer management page or dashboard
        navigate("/manage-dealer-section"); // Adjust this route as needed
      } else {
        toast.error(res?.message || "Failed to add dealer section.");
      }
    } catch (error) {
      toast.error("Failed to add dealer section.");
      console.error("Error adding dealer section:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setFormData({
      builderName: "",
      estdYear: "",
      totalProjects: "",
      shortDescription: "",
      properties: [],
    });
    setSelectedProperties([]);
    setProperties([]);
  };

  // Custom styles for react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: "1px solid #ced4da",
      borderRadius: "0.375rem",
      padding: "2px 8px",
      minHeight: "45px",
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
        : "none",
      borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
      "&:hover": {
        borderColor: "#86b7fe",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e9ecef",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: "0.875rem",
      padding: "4px 8px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      ":hover": {
        backgroundColor: "#dc3545",
        color: "white",
      },
    }),
  };

  return (
    <div className="main_wrap" style={{ marginTop: "6rem" }}>
      <div className="container-fluid">
        <div className="mt-5">
          <div style={headLineStyle} className="page-headline">
            Add Dealer/Builder Section
          </div>
          <hr />

          <section className="chartBox_area">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Dealer/Builder Selection */}
                <div className="col-md-12 mt-3">
                  <label htmlFor="dealerSelect" className="form-label">
                    Select Dealer/Builder<span className="text-danger">*</span>
                  </label>
                  <Select
                    id="dealerSelect"
                    options={dealerNames}
                    onChange={handleDealerChange}
                    placeholder="Search and select a dealer..."
                    isClearable
                    styles={customStyles}
                    isLoading={fetchingDealers}
                    loadingMessage={() => "Loading dealers..."}
                    noOptionsMessage={() => "No dealers found"}
                  />
                  <div className="form-text">
                    Select a dealer/builder from the list
                  </div>
                </div>

                {/* Establishment Year */}
                <div className="col-md-6 mt-3">
                  <label htmlFor="estdYear" className="form-label">
                    Establishment Year<span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="estdYear"
                    className="form-control"
                    name="estdYear"
                    value={formData.estdYear}
                    onChange={handleInputChange}
                    placeholder="e.g., 2010"
                    min="1900"
                    max={new Date().getFullYear()}
                    required
                  />
                  <div className="form-text">
                    Year when the builder was established (1900-
                    {new Date().getFullYear()})
                  </div>
                </div>

                {/* Total Projects */}
                <div className="col-md-6 mt-3">
                  <label htmlFor="totalProjects" className="form-label">
                    Total Projects<span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="totalProjects"
                    className="form-control"
                    name="totalProjects"
                    value={formData.totalProjects}
                    onChange={handleInputChange}
                    placeholder="e.g., 25"
                    min="1"
                    step="1"
                    required
                  />
                  <div className="form-text">
                    Total number of projects completed (positive number)
                  </div>
                </div>

                {/* Short Description */}
                <div className="col-md-12 mt-3">
                  <label htmlFor="shortDescription" className="form-label">
                    Short Description<span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="shortDescription"
                    className="form-control"
                    name="shortDescription"
                    value={formData.shortDescription}
                    onChange={handleInputChange}
                    placeholder="e.g., Leading real estate developer with 25+ successful projects..."
                    rows="3"
                    required
                    maxLength="500"
                  />
                  <div className="form-text">
                    Brief description of the builder (max 500 characters)
                  </div>
                </div>

                {/* Properties Selection (Multi-select) */}
                <div className="col-md-12 mt-3">
                  <label htmlFor="propertiesSelect" className="form-label">
                    Select Properties (with Apartment Names)
                    <span className="text-danger">*</span>
                  </label>
                  <Select
                    id="propertiesSelect"
                    options={properties}
                    value={selectedProperties}
                    onChange={handlePropertyChange}
                    placeholder={
                      fetchingProperties
                        ? "Loading properties..."
                        : formData.builderName
                        ? `Select properties for "${formData.builderName}"`
                        : "Please select a dealer first"
                    }
                    isMulti
                    isClearable
                    isDisabled={
                      !formData.builderName ||
                      fetchingProperties ||
                      properties.length === 0
                    }
                    styles={customStyles}
                    closeMenuOnSelect={false}
                    isLoading={fetchingProperties}
                    loadingMessage={() => "Loading properties..."}
                    noOptionsMessage={() =>
                      fetchingProperties
                        ? "Loading..."
                        : formData.builderName && properties.length === 0
                        ? "No properties with apartment names found"
                        : "Please select a dealer first"
                    }
                  />
                  <div className="form-text">
                    {fetchingProperties ? (
                      <span className="text-info">
                        <i className="fas fa-spinner fa-spin me-1"></i>
                        Loading properties...
                      </span>
                    ) : formData.builderName && properties.length > 0 ? (
                      <span className="text-primary">
                        {/* <i className="fas fa-check-circle me-1"></i>
                        Showing {properties.length} properties with apartment
                        names */}
                      </span>
                    ) : formData.builderName && properties.length === 0 ? (
                      <span className="text-warning">
                        <i className="fas fa-exclamation-circle me-1"></i>
                        No properties with apartment names available for this
                        dealer
                      </span>
                    ) : (
                      <span>
                        <i className="fas fa-info-circle me-1"></i>
                        Please select a dealer first to view properties
                      </span>
                    )}
                  </div>
                </div>

                {/* Selected Properties Preview */}
                {/* {selectedProperties.length > 0 && (
                  <div className="col-md-12 mt-4">
                    <div className="card border-primary">
                      <div className="card-header bg-primary text-white">
                        <h6 className="mb-0">
                          <i className="fas fa-list-check me-2"></i>
                          Selected Properties ({selectedProperties.length})
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {selectedProperties.map((property, index) => (
                            <div
                              key={property.value}
                              className="col-md-3 col-sm-6 mb-2"
                            >
                              <div className="border rounded p-2 bg-light">
                                <small className="d-flex align-items-center">
                                  <i className="fas fa-building me-2 text-primary"></i>
                                  <span className="text-truncate">
                                    {property.label}
                                  </span>
                                </small>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-danger p-0 mt-1"
                                  onClick={() => {
                                    const newSelection =
                                      selectedProperties.filter(
                                        (p) => p.value !== property.value
                                      );
                                    handlePropertyChange(newSelection);
                                  }}
                                >
                                  <small>Remove</small>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )} */}

                {/* Form Summary */}
                {/* {formData.builderName && (
                  <div className="col-md-12 mt-4">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Form Summary</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-3">
                            <strong>Dealer:</strong>
                            <br />
                            <span>{formData.builderName}</span>
                          </div>
                          <div className="col-md-3">
                            <strong>Est. Year:</strong>
                            <br />
                            <span>{formData.estdYear || "Not set"}</span>
                          </div>
                          <div className="col-md-3">
                            <strong>Projects:</strong>
                            <br />
                            <span>{formData.totalProjects || "Not set"}</span>
                          </div>
                          <div className="col-md-3">
                            <strong>Properties:</strong>
                            <br />
                            <span>{selectedProperties.length} selected</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )} */}
              </div>

              {/* Submit Buttons */}
              <div className="row mt-4">
                <div className="col-12 d-flex gap-3">
                  <button
                    type="submit"
                    className="btn"
                    style={{
                      background: "rgb(133, 218, 79)",
                      borderRadius: "13px",
                      padding: "10px 30px",
                      fontSize: "16px",
                      fontWeight: "500",
                      border: "none",
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin me-2"></i>
                        Adding Dealer Section...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-plus-circle me-2"></i>
                        Add Dealer Section
                      </>
                    )}
                  </button>
                  {/* <button
                    type="button"
                    className="btn btn-secondary"
                    style={{
                      borderRadius: "13px",
                      padding: "10px 30px",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                    onClick={clearForm}
                    disabled={loading}
                  >
                    <i className="fas fa-eraser me-2"></i>
                    Clear Form
                  </button> */}
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    style={{
                      borderRadius: "13px",
                      padding: "10px 30px",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                    onClick={fetchDealerNames}
                    disabled={fetchingDealers}
                  >
                    {/* {fetchingDealers ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin me-2"></i>
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sync-alt me-2"></i>
                        Refresh Dealers
                      </>
                    )} */}
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

export default AddDealerSection;
